import { supabase } from "@/api/supabase";
import type { Tables, TablesInsert } from "@/types/database";

/**
 * A company (subcontractor), mapped from the `companies` table row to a
 * camelCase shape. `report_type` is a real column but is unused by the
 * Flutter app (report generation is server-side) and is deliberately left
 * off this type for the same reason — add it back if a screen needs it.
 */
export type Company = {
  id: number;
  name: string;
  email: string | null;
  phoneNumber: string | null;
  address: string | null;
  fee: number;
  currency: string;
  isMoneyCodeEnabled: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

function mapCompany(row: Tables<"companies">): Company {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phoneNumber: row.phone_number,
    address: row.address,
    fee: row.fee,
    currency: row.currency,
    isMoneyCodeEnabled: row.is_money_code_enabled,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/** Active companies only — deleted ones are soft-deleted (archived_at set) and hidden everywhere. */
export async function getCompanies(): Promise<Company[]> {
  const { data, error } = await supabase
    .from("companies")
    .select("*")
    .is("archived_at", null)
    .order("name");
  if (error) {
    throw error;
  }
  return (data ?? []).map(mapCompany);
}

export type CompanyWithCardCount = Company & { cardCount: number };

/**
 * Every company with its assigned-card count, via a single PostgREST
 * embedded-count query (`companies` left-joined to `company_cards`,
 * aggregated server-side) instead of a separate round trip. `company_cards`
 * has a real FK to `companies.id` in the live schema — confirmed against
 * PostgREST directly — even though `database.ts`'s generated `Relationships`
 * is stale and shows none, which is why the embed result needs an explicit
 * type here rather than inferring cleanly from `Tables<"companies">`.
 */
export async function getCompaniesWithCardCount(): Promise<CompanyWithCardCount[]> {
  const { data, error } = await supabase
    .from("companies")
    .select("*, company_cards(count)")
    .is("archived_at", null)
    .order("name")
    .returns<(Tables<"companies"> & { company_cards: { count: number }[] })[]>();
  if (error) {
    throw error;
  }
  return (data ?? []).map((row) => ({
    ...mapCompany(row),
    cardCount: row.company_cards[0]?.count ?? 0,
  }));
}

export async function getCompanyById(id: number): Promise<Company> {
  const { data, error } = await supabase.from("companies")
    .select("*")
    .eq("id", id)
    .is("archived_at", null)
    .single();
  if (error) {
    throw error;
  }
  return mapCompany(data);
}

export type CompanyInput = {
  name: string;
  email: string | null;
  phoneNumber: string | null;
  address: string | null;
  fee: number;
  currency: string;
  isMoneyCodeEnabled: boolean;
};

function toInsertRow(input: CompanyInput): TablesInsert<"companies"> {
  return {
    name: input.name,
    email: input.email,
    phone_number: input.phoneNumber,
    address: input.address,
    fee: input.fee,
    currency: input.currency,
    is_money_code_enabled: input.isMoneyCodeEnabled,
  };
}

export async function addCompany(input: CompanyInput): Promise<Company> {
  const { data, error } = await supabase
    .from("companies")
    .insert(toInsertRow(input))
    .select()
    .single();
  if (error) {
    throw error;
  }
  return mapCompany(data);
}

export async function updateCompany(id: number, input: CompanyInput): Promise<Company> {
  const { data, error } = await supabase
    .from("companies")
    .update({ ...toInsertRow(input), updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return mapCompany(data);
}

/**
 * Soft delete. Hard deletes fail (FK 23503) because money codes and reports
 * reference the company and are history worth keeping. Card assignments are
 * released first so the cards can be given to another company; if the delete
 * step then fails the company is still active and the action can be retried.
 */
export async function deleteCompany(id: number): Promise<void> {
  const released = await supabase.from("company_cards").delete().eq("company_id", id);
  if (released.error) {
    throw released.error;
  }
  const { error } = await supabase
    .from("companies")
    .update({ archived_at: new Date().toISOString() })
    .eq("id", id);
  if (error) {
    throw error;
  }
}
